(() => {
  if (customElements.get('giesto-collection-stories')) return;

  const colorOrders = new Map();
  const productId = product => String(product.id || '').replace('gid://shopify/Product/', '');

  async function colorOrder(id) {
    const cached = globalThis.CSGA?.PC?.m?.get(id);
    if (cached?.products?.length) return cached.products.map(productId);
    if (!colorOrders.has(id)) {
      const pending = (async () => {
        try {
          // giesto-story-index-preload may already have asked for this group
          // while the page was still parsing; read its answer the same way.
          // A failed early request counts as this request failing.
          const preloaded = globalThis.GiestoColorOrderPreload?.get(id);
          let group;
          if (preloaded) {
            globalThis.GiestoColorOrderPreload.delete(id);
            group = await preloaded;
            if (!group) return [];
          } else {
            const shop = globalThis.Shopify?.shop;
            if (!shop) return [];
            const url = new URL('/apps/renk-grubu/get-group', location.origin);
            url.searchParams.set('shop', shop);
            url.searchParams.set('product_id', id);
            const response = await fetch(url, { signal: AbortSignal.timeout(6000) });
            if (!response.ok) return [];
            group = await response.json();
          }
          const ids = (group.products || []).map(productId);
          for (const relatedId of ids) colorOrders.set(relatedId, Promise.resolve(ids));
          return ids;
        } catch { return []; }
      })();
      colorOrders.set(id, pending);
    }
    return colorOrders.get(id);
  }

  class GiestoCollectionStories extends HTMLElement {
    #request;
    #key;
    #loading = false;
    #observer;
    #events;
    #carouselEvents;
    #preload;
    #cachedSource;
    #resume;
    #parserObserver;
    #heroObserver;
    #colorObserver;
    #bootstrap;
    #downloads = new Map();
    static observedAttributes = ['hidden'];

    connectedCallback() {
      this.#events = new AbortController();
      this.addEventListener('click', this.#onClick, { signal: this.#events.signal });
      const start = () => {
        if (!this.querySelector('[data-story-groups]') || !globalThis.GiestoColorGroups) return false;
        this.#parserObserver?.disconnect();
        const catalog = this.closest('[data-gac-story-enabled]');
        if (catalog) delete catalog.dataset.gacStoryFailed;
        if (this.closest('[data-gac-story-enabled]')?.dataset.gacView === 'stories') this.hidden = false;
        if (!this.hidden) this.activate();
        return true;
      };
      if (!start()) {
        this.#parserObserver = new MutationObserver(start);
        this.#parserObserver.observe(this, { childList: true });
        document.addEventListener('giesto:color-groups-ready', start, { once: true, signal: this.#events.signal });
      }
    }

    disconnectedCallback() {
      this.#parserObserver?.disconnect();
      this.#request?.abort();
      this.#preload?.request.abort();
      this.#preload = null;
      this.#observer?.disconnect();
      this.#heroObserver?.disconnect();
      this.#colorObserver?.disconnect();
      this.#events?.abort();
      this.#carouselEvents?.abort();
      this.#loading = false;
      this.#key = null;
    }

    attributeChangedCallback() {
      if (this.isConnected && !this.hidden) this.activate();
    }

    #text(tr, en) { return this.dataset.locale === 'tr' ? tr : en; }

    #slug(value) {
      return String(value || '')
        .toLocaleLowerCase('tr')
        .replaceAll('ı', 'i')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }

    #fit() {
      if (!this.closest('.gac--basic-collection')) return '';
      const match = location.hash.match(/^#fit-([a-z0-9-]+)$/i);
      return match ? match[1].toLowerCase() : '';
    }

    #matchesFit(card, fit) {
      const aliases = {
        'kisa-kollu': ['kisa-kollu', 'kisa-kol', 'short-sleeve'],
        'uzun-kollu': ['uzun-kollu', 'uzun-kol', 'long-sleeve'],
      };
      const identity = this.#slug(`${card.dataset.storyTags || ''}|${card.dataset.storyTitle || ''}`);
      return (aliases[fit] || [fit]).some(token => identity.includes(token));
    }

    #url() {
      // A tagged collection is encoded in the pathname rather than the query.
      // Keep that pathname so editorial view shows the selected subtype instead
      // of silently fetching the entire parent collection.
      const collectionPath = location.pathname.includes('/collections/')
        ? location.pathname
        : this.dataset.collectionUrl;
      const url = new URL(collectionPath, location.origin);
      url.search = location.search;
      for (const key of ['section_id', 'sections']) url.searchParams.delete(key);
      url.searchParams.set('view', 'giesto-stories');
      const theme = globalThis.Shopify?.theme;
      if (theme?.id && theme.role !== 'main' && theme.role !== 'live') url.searchParams.set('preview_theme_id', theme.id);
      url.searchParams.sort();
      const fit = this.#fit();
      if (fit) url.hash = `fit-${fit}`;
      return url;
    }

    #download(url, signal) {
      const keyURL = new URL(url); keyURL.searchParams.sort();
      const key = keyURL.href;
      if (!this.#downloads.has(key)) {
        const pending = this.#downloadUncached(url, signal).catch(error => { this.#downloads.delete(key); throw error; });
        this.#downloads.set(key, pending);
        while (this.#downloads.size > 24) this.#downloads.delete(this.#downloads.keys().next().value);
      }
      return this.#downloads.get(key);
    }

    async #downloadUncached(url, signal) {
      const warm = globalThis.GiestoStoryIndexPreload;
      if (warm && url.searchParams.get('view') === 'giesto-story-index') {
        const key = new URL(url);
        key.searchParams.set('page', 1);
        key.searchParams.sort();
        if (key.href === warm.key) {
          const abort = () => warm.request.abort();
          signal.addEventListener('abort', abort, { once: true });
          try {
            signal.throwIfAborted();
            const html = await warm.fetchPage(Number(url.searchParams.get('page')) || 1);
            signal.throwIfAborted();
            if (html) return html;
          } finally { signal.removeEventListener('abort', abort); }
        }
      }
      const timeout = new AbortController();
      const abort = () => timeout.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) timeout.abort();
      const timer = setTimeout(abort, 12000);
      try {
        const response = await fetch(url, { signal: timeout.signal });
        if (!response.ok) throw new Error(`Editorial page: ${response.status}`);
        return await response.text();
      } finally { clearTimeout(timer); signal.removeEventListener('abort', abort); }
    }

    prefetch() {
      // Only warm the first page on intent, never the whole catalog on visit.
      if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      const url = this.#url();
      if (this.#preload?.key === url.href || (this.#key === url.href && (this.#loading || this.querySelector('.gac-story')))) return;
      this.#preload?.request.abort();
      const request = new AbortController();
      const key = url.href;
      url.searchParams.set('page', 1);
      url.searchParams.set('view', 'giesto-story-index');
      this.#preload = { key, request, promise: this.#download(url, request.signal).catch(() => null) };
    }

    async #yield(signal) {
      // Give input and paint a turn between expensive DOM operations.
      if (globalThis.scheduler?.yield) await scheduler.yield();
      else await new Promise(resolve => setTimeout(resolve, 0));
      signal.throwIfAborted();
    }

    #family(record) {
      const [id, title, color, type, vendor] = record;
      const model = globalThis.GiestoColorGroups.model(title, color);
      return model ? JSON.stringify([vendor, type, model]) : `product:${id}`;
    }

    async #earlyLead(url, signal) {
      // Never guess a representative: prove the global canonical lead is in
      // the current filtered native cards. EN, Basic, page N and mixed search
      // keep the complete-index path until their identity can be proved here.
      if (this.dataset.locale !== 'tr' || this.dataset.source === 'search' || this.#fit() || Number(url.searchParams.get('page') || 1) !== 1 || this.closest('.gac--basic-collection') || this.dataset.initialPath !== location.pathname) return null;
      const initial = [...this.closest('giesto-store-collection')?.querySelectorAll('results-list [ref="grid"] > [data-story-product]') || []];
      if (!initial.length) return null;
      let records = initial.map(card => [card.dataset.storyProduct, card.dataset.storyTitle, card.dataset.storyColor, card.dataset.storyType, card.dataset.storyVendor, card.dataset.storyTags, card.dataset.storySeason === 'fw27' ? ['tumunu-gor-f-w27'] : []]);
      const landingQuery = document.body.dataset.collectionSource && ([...url.searchParams.keys()].some(key => key.startsWith('filter.')) || (url.searchParams.get('sort_by') && url.searchParams.get('sort_by') !== this.dataset.sortOrder));
      // The FW27 Page shell has unfiltered variant/price metadata. Matching
      // only a product ID cannot prove that metadata, so query states retain
      // the complete-index/filtered-section path instead of an early guess.
      if (landingQuery) return null;
      const family = this.#family(records[0]);
      const firstRecords = records.filter(record => this.#family(record) === family);
      const cards = initial.filter(card => firstRecords.some(record => String(record[0]) === card.dataset.storyProduct));
      let leadID;
      const masterManual = url.pathname.endsWith('/collections/tumunu-gor-f-w27') && (url.searchParams.get('sort_by') || this.dataset.sortOrder) === 'manual';
      if (masterManual) leadID = String(records[0][0]);
      else if (firstRecords.some(record => (record[6] || []).includes('tumunu-gor-f-w27'))) {
        let prefix = [];
        try { prefix = JSON.parse(this.querySelector('[data-story-season-prefix]')?.textContent || '[]'); } catch {}
        let canonical = prefix.find(record => this.#family(record) === family);
        if (!canonical) {
          const season = await globalThis.GiestoColorGroups.seasonCatalog();
          signal.throwIfAborted();
          canonical = season.find(record => this.#family(record) === family);
        }
        leadID = String(canonical?.[0] || '');
      } else {
        const ids = await colorOrder(String(records[0][0]));
        signal.throwIfAborted();
        // The first app record is a proven lead only when it is a matching
        // member of this filtered result. Missing/failing app data is fallback.
        leadID = ids[0];
      }
      const source = cards.find(card => card.dataset.storyProduct === leadID);
      if (!source) return null;
      const lead = source.cloneNode(true); lead.dataset.storyFamily = family;
      const story = this.#makeStory([lead], 0, 'spread', lead);
      const hero = story.querySelector('.gac-story__hero');
      story.querySelector('.gac-story__hero-frame')?.remove();
      story.classList.remove('gac-story--single-framed');
      const swatchReservation = document.createElement('ul');
      swatchReservation.className = 'gac-story__lead-swatches';
      swatchReservation.setAttribute('aria-hidden', 'true');
      story.querySelector('.gac-story__lead-meta').append(swatchReservation);
      story.append(hero);
      const colorsReservation = this.querySelector('[data-story-loading] .gac-story__colors')?.cloneNode(true);
      if (colorsReservation) { colorsReservation.classList.add('gac-stories__skeleton'); colorsReservation.setAttribute('aria-hidden', 'true'); story.append(colorsReservation); }
      story.dataset.storyBootstrap = leadID;
      story.dataset.storyLead = leadID;
      return { key: url.href, family, leadID, story };
    }

    // The index is complete before grouping. Only full card/media pages are
    // progressive: filters, search totals and family membership never depend
    // on which part of the catalogue the shopper has scrolled through.
    async activate() {
      const url = this.#url();
      const key = url.href;
      if (key === this.#key && (this.#loading || this.querySelector('.gac-story'))) { this.#resume?.(); return; }
      this.#request?.abort();
      this.#downloads.clear();
      this.#observer?.disconnect();
      this.#heroObserver?.disconnect();
      this.#colorObserver?.disconnect();
      this.#carouselEvents?.abort();
      this.#carouselEvents = new AbortController();
      const request = new AbortController();
      this.#request = request;
      this.#key = key;
      this.#loading = true;
      const status = this.querySelector('[data-story-status]');
      const groups = this.querySelector('[data-story-groups]');
      if (!status || !groups) { this.#loading = false; this.#key = null; return; }
      const skeleton = this.querySelector('[data-story-loading]');
      const back = this.querySelector('[data-story-back]');
      status.hidden = true;
      if (skeleton) skeleton.hidden = !!groups.children.length;
      if (back) back.hidden = true;
      this.querySelector('[data-story-retry]').hidden = true;
      this.setAttribute('aria-busy', 'true');
      const early = this.#earlyLead(url, request.signal).then(bootstrap => {
        if (!bootstrap || request.signal.aborted || this.#request !== request) return null;
        this.#bootstrap = bootstrap;
        groups.replaceChildren(bootstrap.story);
        this.#fadeIn(bootstrap.story);
        if (skeleton) skeleton.hidden = true;
        this.dispatchEvent(new CustomEvent('giesto:stories-lead-ready', { bubbles: true, detail: { product: bootstrap.leadID } }));
        return bootstrap;
      }).catch(() => null);
      try {
        const sourceUrl = new URL(url);
        sourceUrl.hash = '';
        sourceUrl.searchParams.delete('page');
        const sourceKey = sourceUrl.href;
        let cached = this.#cachedSource?.key === sourceKey ? this.#cachedSource : null;
        if (!cached) {
          // Translated storefronts also need the canonical Turkish identities.
          // They do not depend on the index, so both downloads run together.
          const canonicalFamilies = this.dataset.locale === 'tr' ? Promise.resolve(new Map()) : (async () => {
            const canonical = new Map();
            const familyPath = this.dataset.source === 'search' ? '/collections/tum-urunler' : url.pathname.slice(url.pathname.indexOf('/collections/'));
            const familyUrl = new URL(familyPath, location.origin);
            familyUrl.searchParams.set('view', 'giesto-story-families');
            if (url.searchParams.has('preview_theme_id')) familyUrl.searchParams.set('preview_theme_id', url.searchParams.get('preview_theme_id'));
            let pages = 1;
            for (let page = 1; page <= pages; page++) {
              familyUrl.searchParams.set('page', page);
              const doc = new DOMParser().parseFromString(await this.#download(familyUrl, request.signal), 'text/html');
              const data = JSON.parse(doc.querySelector('[data-story-families]').textContent);
              pages = data.pages;
              for (const [id, title, color, type, vendor] of data.products) canonical.set(String(id), { title, color, type, vendor });
              await this.#yield(request.signal);
            }
            return canonical;
          })();
          // Settled here; awaited below once the index is complete.
          canonicalFamilies.catch(() => {});
          const indexUrl = new URL(sourceUrl);
          indexUrl.searchParams.set('view', 'giesto-story-index');
          const records = [];
          let pages = 1;
          for (let page = 1; page <= pages; page++) {
            indexUrl.searchParams.set('page', page);
            const preload = page === 1 && this.#preload?.key === key ? this.#preload : null;
            const html = (preload && await preload.promise) || await this.#download(indexUrl, request.signal);
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const data = JSON.parse(doc.querySelector('[data-story-index]').textContent);
            if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100 || (data.pages === 0 && data.products.length)) throw new Error('Invalid editorial index');
            pages = Math.max(1, data.pages);
            records.push(...data.products);
            await this.#yield(request.signal);
          }
          const canonical = await canonicalFamilies;
          cached = { key: sourceKey, records, canonical, cards: new Map(), pages: new Map() };
          this.#cachedSource = cached;
        }
        // Grid cards are already server-filtered. Keep pristine copies so view
        // switches, retries and fitting filters do not consume their markup.
        if (this.dataset.initialPath === location.pathname) {
          const initialCards = this.closest('giesto-store-collection')?.querySelectorAll('results-list [ref="grid"] > [data-story-product]') || [];
          // Keep the pristine copies in an inert document, like fetched pages.
          // A detached copy owned by this document can start its image download
          // before it is ever shown; the cache used to fetch the feature cells'
          // full-width candidates ahead of the lead product's own image.
          const inert = document.implementation.createHTMLDocument();
          for (const card of initialCards) if (!cached.cards.has(card.dataset.storyProduct)) cached.cards.set(card.dataset.storyProduct, inert.importNode(card, true));
        }
        // #orderFamilies reads the FW27 master order whenever a family holds a
        // FW27 product outside Basic, except on the master collection's own
        // manual order, which it already has. That order is memoised, so asking
        // now only moves the request ahead of the first family's colours.
        const masterManualSource = sourceUrl.pathname.endsWith('/collections/tumunu-gor-f-w27') && this.dataset.locale === 'tr' && (sourceUrl.searchParams.get('sort_by') || this.dataset.sortOrder) === 'manual';
        const basicSource = this.closest('.gac--basic-collection') || this.closest('giesto-collection-hub[data-mode="basic"]');
        if (!basicSource && !masterManualSource && cached.records.some(record => (record[6] || []).includes('tumunu-gor-f-w27'))) globalThis.GiestoColorGroups?.seasonOrder?.();
        const families = new Map();
        const positions = new Map();
        const productHandles = new Map();
        const fit = this.#fit();
        const offset = Math.max(0, (Number(url.searchParams.get('page')) || 1) - 1) * (Number(this.dataset.pageSize) || 24);
        const eligible = new Set();
        const firstOrdinal = new Map();
        cached.records.forEach(([id, title, color, type, vendor, tags, handles, sourcePosition, productHandle], position) => {
          id = String(id);
          if (productHandle) productHandles.set(id, productHandle);
          // Search may interleave articles/pages. Card-page boundaries belong
          // to the original Shopify result order, not the product-only index.
          const ordinal = Number.isInteger(sourcePosition) ? sourcePosition : position;
          positions.set(id, Math.floor(ordinal / 100) + 1);
          const card = { dataset: { storyProduct: id, storyTitle: title, storyColor: color, storyType: type, storyVendor: vendor, storyTags: tags || '', storySeason: (handles || []).includes('tumunu-gor-f-w27') ? 'fw27' : '' } };
          if (fit && !this.#matchesFit(card, fit)) return;
          const identity = cached.canonical.get(id) || { title, color, type, vendor };
          const model = globalThis.GiestoColorGroups?.model(identity.title, identity.color);
          const family = model ? JSON.stringify([identity.vendor, identity.type, model]) : `product:${id}`;
          card.dataset.storyFamily = family;
          if (!families.has(family)) families.set(family, []);
          families.get(family).push(card);
          if (!firstOrdinal.has(family)) firstOrdinal.set(family, ordinal);
          if (ordinal >= offset) eligible.add(family);
        });
        // Anchor page N to the first product on that grid page while retaining
        // complete colour families. Earlier pages remain available via links.
        const ordered = this.#personalOrder([...eligible].map(family => families.get(family)), firstOrdinal);
        const loadPage = page => {
          if (!cached.pages.has(page)) {
            const promise = (async () => {
              const pageUrl = new URL(sourceUrl);
              pageUrl.searchParams.set('page', page);
              const doc = new DOMParser().parseFromString(await this.#download(pageUrl, request.signal), 'text/html');
              const result = doc.querySelector('[data-story-page]');
              if (!result) throw new Error('Missing editorial collection response');
              for (const card of result.querySelectorAll('[data-story-product]')) cached.cards.set(card.dataset.storyProduct, card);
              await this.#yield(request.signal);
            })().catch(error => { cached.pages.delete(page); throw error; });
            cached.pages.set(page, promise);
          }
          return cached.pages.get(page);
        };
        // One product's card on its own (product.giesto-story-card), rendered
        // through the same snippet as the 100-card pages: under the collection
        // path for a collection, at /products/ for search.
        const loadCard = id => {
          const key = `product:${id}`;
          if (!cached.pages.has(key)) {
            const promise = (async () => {
              const handle = productHandles.get(id);
              if (!handle) return;
              const path = sourceUrl.pathname;
              const base = path.match(/^.*\/collections\/[^/]+/)?.[0] ?? path.replace(/\/search\/?$/, '');
              const cardUrl = new URL(`${base}/products/${encodeURIComponent(handle)}`, location.origin);
              cardUrl.searchParams.set('view', 'giesto-story-card');
              if (sourceUrl.searchParams.has('preview_theme_id')) cardUrl.searchParams.set('preview_theme_id', sourceUrl.searchParams.get('preview_theme_id'));
              const doc = new DOMParser().parseFromString(await this.#download(cardUrl, request.signal), 'text/html');
              const card = doc.querySelector(`[data-story-product="${id}"]`);
              if (card) cached.cards.set(id, card);
            })().catch(error => { cached.pages.delete(key); throw error; });
            cached.pages.set(key, promise);
          }
          return cached.pages.get(key);
        };
        const bootstrap = await early;
        request.signal.throwIfAborted();
        const fragment = document.createDocumentFragment();
        if (offset > 0) {
          const previous = document.createElement('a');
          const previousUrl = new URL(location.href);
          previousUrl.searchParams.set('page', Math.max(1, (Number(url.searchParams.get('page')) || 1) - 1));
          previous.href = previousUrl.href;
          previous.className = 'gac-stories__previous';
          previous.textContent = this.#text('Önceki sayfa', 'Previous page');
          fragment.append(previous);
        }
        const slots = ordered.map((cards, index) => {
          const retained = index === 0 && bootstrap?.family === cards[0].dataset.storyFamily ? bootstrap.story : null;
          const slot = retained || this.#section('spread', cards[0].dataset.storyTitle);
          if (!retained) slot.dataset.storyPending = String(index);
          slot.dataset.family = cards[0].dataset.storyFamily;
          slot.setAttribute('aria-busy', 'true');
          if (index === 0 && skeleton && !retained) {
            slot.classList.add('gac-stories__skeleton');
            slot.append(...skeleton.cloneNode(true).childNodes);
          }
          if (index > 0) {
            const load = document.createElement('button');
            load.type = 'button';
            load.className = 'gac-stories__load';
            load.dataset.storyLoad = String(index);
            load.textContent = this.#text('Ürünleri göster', 'Show products');
            load.setAttribute('aria-label', `${load.textContent}: ${cards[0].dataset.storyTitle}`);
            slot.append(load);
          }
          fragment.append(slot);
          return slot;
        });
        // One atomic swap only when the source/fit/page changes. Enrichment of
        // this source never removes a mounted group or touches its hero.
        groups.replaceChildren(fragment);
        // The first slot now owns the reservation. Keeping the original above
        // it until media arrived doubled the height, then shifted the hero up.
        if (skeleton) skeleton.hidden = true;
        this.dataset.productCount = String([...families.values()].reduce((sum, cards) => sum + cards.length, 0));
        this.dataset.familyCount = String(ordered.length);
        if (!ordered.length) {
          status.hidden = false;
          status.textContent = this.#text('Ürün bulunamadı.', 'No products found.');
          if (back) back.hidden = false;
        }
        const hydrate = async index => {
          const descriptors = ordered[index];
          const needed = [...new Set(descriptors.filter(card => !cached.cards.has(card.dataset.storyProduct)).map(card => positions.get(card.dataset.storyProduct)))];
          // The colour order needs only the family's first product, which
          // #orderFamilies asks for below; ask now so it arrives with the cards.
          if (descriptors.length > 1) colorOrder(descriptors[0].dataset.storyProduct);
          // Only the pages holding this family's products are fetched, at most
          // three at a time; one after another, search waited on each in turn.
          for (let next = 0; next < needed.length; next += 3) await Promise.all(needed.slice(next, next + 3).map(loadPage));
          // Shopify's search order is not stable between differently paginated
          // requests: a product can sit on another page than the index says, or
          // on none. Such a family used to end on a retry that could never
          // succeed; its missing products now come as single cards.
          const missing = descriptors.map(card => card.dataset.storyProduct).filter(id => !cached.cards.has(id));
          if (missing.length) await Promise.all(missing.map(loadCard));
          const cards = descriptors.map(descriptor => {
            const source = cached.cards.get(descriptor.dataset.storyProduct);
            if (!source) throw new Error('Missing indexed product');
            const card = source.cloneNode(true);
            card.className = 'gac-story__color';
            card.removeAttribute('id');
            card.removeAttribute('style');
            card.dataset.storyFamily = descriptor.dataset.storyFamily;
            return card;
          });
          const masterManual = sourceUrl.pathname.endsWith('/collections/tumunu-gor-f-w27') && this.dataset.locale === 'tr' && (sourceUrl.searchParams.get('sort_by') || this.dataset.sortOrder) === 'manual';
          const [{ cards: sorted, lead }] = await Promise.all(this.#orderFamilies(cards.length ? [cards] : [], masterManual ? cached.records.map(record => String(record[0])) : null));
          request.signal.throwIfAborted();
          const story = this.#makeStory(sorted, index, 'spread', lead);
          const slot = slots[index];
          // The slot keeps its identity and position. Its contents mount once.
          const restoreFocus = slot.contains(document.activeElement);
          slot.setAttribute('aria-label', story.getAttribute('aria-label'));
          const hero = slot.querySelector('.gac-story__hero');
          if (slot.dataset.storyBootstrap && lead.dataset.storyProduct === slot.dataset.storyBootstrap && hero) {
            story.querySelector('.gac-story__hero').replaceWith(hero);
          }
          slot.replaceChildren(...story.childNodes);
          for (const name of ['gac-story--single-lead', 'gac-story--single-framed']) slot.classList.toggle(name, story.classList.contains(name));
          slot.dataset.storyLead = lead.dataset.storyProduct;
          delete slot.dataset.storyBootstrap;
          slot.removeAttribute('data-story-pending');
          slot.classList.remove('gac-stories__skeleton');
          slot.removeAttribute('aria-busy');
          this.#fadeIn(slot);
          this.#mountColors(slot);
          if (restoreFocus) slot.querySelector('.gac-story__lead-link')?.focus({ preventScroll: true });
          if (index === 0) {
            if (fit) slot.id = `fit-${fit}`;
            this.dispatchEvent(new CustomEvent('giesto:stories-ready', { bubbles: true, detail: { fit } }));
          }
        };
        // Resolve the first family before scheduling adjacent groups. This
        // protects its poster/image from catalogue and colour-request workers.
        if (slots.length) await hydrate(0);
        if (skeleton) skeleton.hidden = true;
        const queue = [];
        const queued = new Set();
        let running = false;
        const drain = async () => {
          if (running) return;
          running = true;
          while (queue.length && !request.signal.aborted && !this.hidden) {
            const index = queue.shift();
            try { await hydrate(index); }
            catch (error) {
              if (request.signal.aborted) break;
              console.warn('[Giesto collection story]', error);
              queued.delete(index);
              const retry = document.createElement('button');
              retry.type = 'button';
              retry.className = 'gac-stories__retry';
              retry.textContent = this.#text('Bu ürünleri tekrar yükle', 'Retry these products');
              retry.addEventListener('click', () => { retry.remove(); enqueue(index); }, { once: true, signal: request.signal });
              slots[index].append(retry);
            }
          }
          running = false;
        };
        this.#resume = drain;
        const enqueue = index => {
          if (queued.has(index) || request.signal.aborted) return;
          queued.add(index);
          queue.push(index);
          drain();
        };
        this.#observer = new IntersectionObserver(entries => {
          for (const entry of entries) if (entry.isIntersecting && !this.hidden) {
            const index = Number(entry.target.dataset.storyPending);
            this.#observer.unobserve(entry.target);
            enqueue(index);
          }
        }, { rootMargin: '300px' });
        for (const [index, slot] of slots.entries()) if (index > 0) {
          const load = slot.querySelector('[data-story-load]');
          // Keyboard focus is loading intent too. Keep a real control in each
          // pending region so Tab cannot skip the catalogue to the footer.
          load.addEventListener('focus', () => enqueue(index), { signal: request.signal });
          load.addEventListener('click', () => enqueue(index), { signal: request.signal });
          this.#observer.observe(slot);
        }
      } catch (error) {
        if (request.signal.aborted) return;
        this.#key = null;
        status.hidden = false;
        status.textContent = this.#text('Bu görünüm yüklenemedi. Lütfen tekrar deneyin.', 'This view could not load. Please try again.');
        if (back) back.hidden = false;
        this.querySelector('[data-story-retry]').hidden = false;
      } finally {
        if (this.#request === request) {
          this.#preload?.request.abort();
          this.#preload = null;
          this.#loading = false;
          if (skeleton) skeleton.hidden = true;
          this.removeAttribute('aria-busy');
        }
      }
    }

    // Photographs still downloading stay transparent over their grey frame
    // and fade in once decoded (giesto-collection-stories.css). An image that
    // is already complete, including from cache, is left untouched.
    #fadeIn(root) {
      for (const img of root.querySelectorAll('img')) {
        if (img.complete || img.dataset.storyFade !== undefined) continue;
        img.dataset.storyFade = '';
        const shown = () => { delete img.dataset.storyFade; };
        img.addEventListener('load', shown, { once: true });
        img.addEventListener('error', shown, { once: true });
      }
    }

    #section(layout, label) {
      const section = document.createElement('section');
      section.className = `gac-story gac-story--${layout}`;
      section.dataset.storyLayout = layout;
      section.setAttribute('aria-label', label);
      return section;
    }

    // YUN-91: group B of the `kisisel-siralama` A/B test, with a taste profile
    // (snippets/giesto-affinity.liquid), sees the families that match its fit
    // and colour moved up within each grid page. The store's season order
    // breaks ties, and families never cross a page boundary. The first family
    // stays first: its hero is already painting from the early lead.
    #personalOrder(ordered, firstOrdinal) {
      const ilgi = globalThis.GiestoIlgi;
      if (!ilgi?.active() || ordered.length < 3) return ordered;
      // A sort the shopper picked (price, newest) is theirs to keep.
      const chosenSort = new URL(location.href).searchParams.get('sort_by');
      if (chosenSort && chosenSort !== this.dataset.sortOrder) return ordered;
      const pageSize = Number(this.dataset.pageSize) || 24;
      const page = cards => Math.floor((firstOrdinal.get(cards[0].dataset.storyFamily) || 0) / pageSize);
      // A family is as good as its best-matching colour.
      const signatureOf = cards => cards
        .map(card => ilgi.signature(card.dataset.storyTitle, card.dataset.storyTags, card.dataset.storyColor, card.dataset.storyType))
        .reduce((best, signature) => (ilgi.score(signature) > ilgi.score(best) ? signature : best));
      const [lead, ...rest] = ordered;
      const result = [lead];
      for (let start = 0; start < rest.length;) {
        let end = start + 1;
        while (end < rest.length && page(rest[end]) === page(rest[start])) end++;
        result.push(...ilgi.rank(rest.slice(start, end), signatureOf));
        start = end;
      }
      // Marked only when something moved, so clicks and the weekly report can
      // tell a personal order from the store's (giesto-funnel.js).
      if (result.some((cards, index) => cards !== ordered[index])) {
        this.setAttribute('data-ilgi-sirali', '');
        ilgi.applied?.(ilgi.onSearch?.() ? 'arama' : 'koleksiyon');
      } else this.removeAttribute('data-ilgi-sirali');
      return result;
    }

    #orderFamilies(families, knownSeasonIds = null) {
      // Shopify's FW27 master collection chooses its representative product
      // across season pages, categories, all-products and search. Keep the
      // app order for the bottom colour cards and for Basic's presentation.
      // Both choices are restricted to products in the current filtered result.
      const isBasic = this.closest('.gac--basic-collection') || this.closest('giesto-collection-hub[data-mode="basic"]');
      const useSeasonOrder = !isBasic &&
        families.some(cards => cards.some(card => card.dataset.storySeason === 'fw27'));
      const seasonOrder = useSeasonOrder ? (knownSeasonIds ? Promise.resolve(knownSeasonIds) : globalThis.GiestoColorGroups?.seasonOrder?.() || Promise.resolve([])) : Promise.resolve([]);
      let next = 0;
      const resolve = [];
      const pending = families.map((_, index) => new Promise(done => { resolve[index] = done; }));
      const worker = async () => {
        const seasonIds = await seasonOrder;
        const seasonRanks = new Map(seasonIds.map((id, rank) => [id, rank]));
        while (next < families.length) {
          const index = next++;
          const cards = families[index];
          const ids = cards.length > 1 ? await colorOrder(cards[0].dataset.storyProduct) : [];
          const ranks = new Map(ids.map((id, rank) => [id, rank]));
          const sorted = cards.slice().sort((a, b) =>
            (ranks.get(a.dataset.storyProduct) ?? ids.length) - (ranks.get(b.dataset.storyProduct) ?? ids.length));
          const seasonLeadId = cards.filter(card => card.dataset.storySeason === 'fw27' && seasonRanks.has(card.dataset.storyProduct)).reduce((best, card) => !best || seasonRanks.get(card.dataset.storyProduct) < seasonRanks.get(best.dataset.storyProduct) ? card : best, null)?.dataset.storyProduct;
          resolve[index]({ cards: sorted, lead: cards.find(card => card.dataset.storyProduct === seasonLeadId) || sorted[0] });
        }
      };
      for (let index = 0; index < Math.min(6, families.length); index++) worker();
      return pending;
    }

    #frames(card) {
      const frames = [...card.querySelectorAll('.gac-card__frame')];
      for (const template of card.querySelectorAll('template[data-gac-gallery-frames]')) frames.push(...template.content.querySelectorAll('.gac-card__frame'));
      return frames;
    }

    #prepareCard(card, packshot, colorCard = false) {
      const gallery = card.querySelector('[data-gac-gallery]');
      // A single-media card has no gallery wrapper. Its deferred frame must
      // still mount before sizing the card or positioning the favorite icon.
      if (!gallery) {
        const deferred = card.querySelector('.gac-card__media-link > template[data-gac-gallery-frames]');
        if (deferred) deferred.replaceWith(deferred.content);
      }
      if (packshot && gallery) {
        // Image policy belongs to the product, including on mixed catalogue pages.
        // FW27 starts with its first photograph; Basic keeps its last photo.
        // Active Wear starts with the cut-out in the admin's second media slot,
        // where a video counts as a slot, so it is found by admin media order.
        // Video previews are excluded from these small colour cards.
        const frames = this.#frames(card);
        const photos = frames.filter(frame => frame.dataset.mediaType === 'image');
        const order = card.dataset.storyColorImageOrder;
        const secondId = (card.dataset.storyMediaOrder || '').split(',')[1];
        const second = photos.find(frame => frame.dataset.mediaId === secondId) ?? photos[1] ?? photos[0];
        const startingPhoto = order === 'first' ? photos[0] : order === 'second' ? second : photos.at(-1);
        if (startingPhoto) {
          const deferred = gallery.querySelector('template[data-gac-gallery-frames]');
          if (deferred) for (const frame of [...gallery.children].filter(frame => frame.classList.contains('gac-card__frame') && frame !== startingPhoto)) deferred.content.prepend(frame);
          gallery.prepend(startingPhoto);
          gallery.scrollLeft = 0;
          card.querySelector('[data-gac-gallery-prev]')?.setAttribute('disabled', '');
          card.querySelector('[data-gac-gallery-next]')?.toggleAttribute('disabled', frames.length < 2);
        }
      }
      for (const img of card.querySelectorAll('.gac-card__frame img')) {
        img.sizes = packshot ? '(min-width: 1025px) 19vw, 44vw' : '(min-width: 1025px) 25vw, 70vw';
        img.loading = 'lazy';
        img.fetchPriority = 'low';
      }
      const first = card.querySelector('.gac-card__frame img');
      if (first) first.alt = card.dataset.storyTitle;
      // These families are already resolved from the active collection. Avoid
      // an app rescan replacing them with unrelated or unfiltered products.
      if (colorCard) {
        // A color card already represents one choice in the resolved family.
        // Native Shopify selectors must not create a second family inside it.
        card.querySelectorAll('.gac-card__colour-groups, .gac-card__swatches').forEach(selector => selector.remove());
      } else {
        card.querySelector('[data-gac-swatch-sync]')?.closest('.gac-card__colour-groups')?.remove();
      }
      const favorite = card.querySelector('giesto-wishlist-toggle');
      if (favorite) {
        favorite.classList.add('gac-story__favorite');
        card.querySelector('.gac-card__media').append(favorite);
        card.querySelector('.gac-card__quick-divider')?.remove();
      }
      card.querySelector('.gac-card__meta').title = card.dataset.storyTitle;
      return card;
    }

    #makeLeadMeta(cards, lead = cards[0]) {
      const sourceMeta = lead.querySelector('.gac-card__meta');
      const href = sourceMeta?.getAttribute('href') || '#';
      const meta = document.createElement('header');
      meta.className = 'gac-story__lead-meta';

      const link = document.createElement('a');
      link.className = 'gac-story__lead-link';
      link.href = href;

      const title = document.createElement('span');
      title.className = 'gac-story__lead-title';
      title.textContent = lead.dataset.storyTitle;
      link.append(title);

      const sourcePrice = sourceMeta?.querySelector('.gac-card__price-row');
      if (sourcePrice) {
        const price = document.createElement('span');
        price.className = 'gac-story__lead-price';
        price.classList.toggle('gac-story__lead-price--sale', sourcePrice.classList.contains('gac-card__price-row--sale'));
        price.append(...sourcePrice.cloneNode(true).childNodes);
        const quantity = lead.querySelector('.gac-card__price > [data-sale-quantity-price]');
        if (quantity) price.append(quantity.cloneNode(true));
        link.append(price);
      }

      const swatches = document.createElement('ul');
      swatches.className = 'gac-story__lead-swatches';
      swatches.setAttribute('aria-label', this.#text('Renk seçenekleri', 'Color options'));
      for (const card of cards) {
        const swatch = document.createElement('a');
        swatch.className = 'gac-story__lead-swatch';
        swatch.href = card.querySelector('.gac-card__meta')?.getAttribute('href') || href;
        swatch.title = card.dataset.storyColor || card.dataset.storyTitle;
        swatch.setAttribute('aria-label', card.dataset.storyTitle);
        const item = document.createElement('li');
        if (card === lead) swatch.setAttribute('aria-current', 'true');
        const chip = document.createElement('span');
        chip.style.background = card.dataset.storyChip || '#d8d8d8';
        swatch.append(chip);
        item.append(swatch);
        swatches.append(item);
      }

      meta.append(link, swatches);
      return meta;
    }

    #deferBehindBanner(hero) {
      const banner = document.querySelector('[data-activewear-banner] img') ||
        (document.body.dataset.collectionSource && document.querySelector('#ge-new .ge-campaign-image img'));
      if (!banner || (banner.complete && banner.naturalWidth)) return;
      // Campaign players own their lazy posters and readiness state. Removing
      // their src here would make an empty image look complete to the player.
      const images = [...hero.querySelectorAll(':scope > img')];
      for (const image of images) {
        if (image.hasAttribute('src')) { image.dataset.storyDeferredSrc = image.getAttribute('src'); image.removeAttribute('src'); }
        if (image.hasAttribute('srcset')) { image.dataset.storyDeferredSrcset = image.getAttribute('srcset'); image.removeAttribute('srcset'); }
      }
      const restore = () => {
        if (!hero.isConnected) return;
        this.#heroObserver?.unobserve(hero);
        for (const image of images) {
          if (image.dataset.storyDeferredSrcset) { image.srcset = image.dataset.storyDeferredSrcset; delete image.dataset.storyDeferredSrcset; }
          if (image.dataset.storyDeferredSrc) { image.src = image.dataset.storyDeferredSrc; delete image.dataset.storyDeferredSrc; }
        }
        this.#fadeIn(hero);
      };
      const signal = this.#request.signal;
      banner.addEventListener('load', restore, { once: true, signal });
      banner.addEventListener('error', restore, { once: true, signal });
      if (!this.#heroObserver) this.#heroObserver = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) entry.target.dispatchEvent(new Event('giesto:hero-visible'));
      });
      hero.addEventListener('giesto:hero-visible', restore, { signal });
      hero.addEventListener('focus', restore, { signal });
      this.#heroObserver.observe(hero);
    }

    #makeStory(cards, index, layout, lead = cards[0]) {
      const story = this.#section(layout, lead.dataset.storyTitle);
      story.dataset.family = lead.dataset.storyFamily;
      story.dataset.storyLead = lead.dataset.storyProduct;
      const leadMeta = this.#makeLeadMeta(cards, lead);
      const hero = document.createElement('a');
      hero.className = 'gac-story__hero';
      hero.href = lead.querySelector('.gac-card__meta').getAttribute('href');
      hero.setAttribute('aria-label', lead.dataset.storyTitle);
      const mediaOrder = new Map((lead.dataset.storyMediaOrder || '').split(',').map((id, position) => [id, position]));
      const frames = this.#frames(lead).sort((a, b) =>
        (mediaOrder.get(a.dataset.mediaId) ?? mediaOrder.size) - (mediaOrder.get(b.dataset.mediaId) ?? mediaOrder.size));
      const media = frames.map(frame => frame.querySelector(':scope > img, :scope > video')).filter(Boolean);
      // Use the first two Admin media items in order, left then right. Card
      // galleries may move videos or packshots first; that doesn't reorder here.
      const detailVideo = layout !== 'portrait' && lead.dataset.storyDetailVideo && media[0]?.tagName === 'IMG';
      const heroMedia = media.slice(0, detailVideo || layout === 'portrait' ? 1 : 2);
      // Active Wear keeps its Admin video first but shows it on the right.
      if (lead.dataset.storyVideoSide === 'right' && heroMedia.length === 2 && heroMedia[0].tagName === 'VIDEO' && heroMedia[1].tagName === 'IMG') heroMedia.reverse();
      const multiple = Boolean(detailVideo || heroMedia.length > 1);
      const imageSizes = multiple ? '(min-width: 1025px) 26.25vw, (min-width: 750px) 40vw, 50vw' : '(min-width: 1025px) 37.8vw, (min-width: 750px) 57.6vw, 100vw';
      const mediaFraction = innerWidth > 1024 ? (multiple ? 0.2625 : 0.378) : innerWidth >= 750 ? (multiple ? 0.4 : 0.576) : (multiple ? 0.5 : 1);
      // Both halves show the same colourway; retain the actual photograph's
      // proportions when there is no landscape campaign image in our catalog.
      const leading = index === 0 && !document.body.dataset.collectionSource;
      for (const source of heroMedia) {
        const img = source.cloneNode(true);
        if (img.tagName === 'IMG') img.alt = '';
        else {
          img.setAttribute('aria-label', lead.dataset.storyTitle);
          const width = Math.min(1600, Math.max(400, Math.ceil(innerWidth * mediaFraction * (devicePixelRatio || 1) / 100) * 100));
          let poster = img.getAttribute('poster')?.replace(/_\d+x(?=\.)/, `_${width}x`);
          if (poster) { const posterURL = new URL(poster, location.origin); if (posterURL.searchParams.has('width')) { posterURL.searchParams.set('width', width); poster = posterURL.href; } }
          if (poster) {
            img.poster = poster;
            img.querySelector('img')?.setAttribute('src', poster);
            if (leading) {
              const preload = document.createElement('link');
              preload.rel = 'preload'; preload.as = 'image'; preload.href = poster; preload.fetchPriority = 'high';
              hero.append(preload);
            }
          }
        }
        img.sizes = imageSizes;
        img.loading = leading ? 'eager' : 'lazy';
        img.fetchPriority = leading ? 'high' : 'low';
        img.dataset.storyMediaId = source.closest('.gac-card__frame')?.dataset.mediaId || '';
        hero.append(img);
      }
      if (detailVideo) {
        // Only a clip for this exact product/colour replaces the right photo.
        // Reuse the homepage player: no source until visible, poster fallback,
        // muted playback and pause offscreen/reduced-motion/Save Data.
        hero.classList.add('gac-story__hero--detail');
        const player = document.createElement('giesto-campaign-video');
        player.className = 'gac-story__detail-video';
        player.setAttribute('data-poster-skeleton', '');
        for (const variant of ['desktop', 'mobile']) {
          player.dataset[variant + 'Src'] = variant === 'mobile' ? lead.dataset.storyDetailMobileVideo : lead.dataset.storyDetailVideo;
          player.dataset[variant + 'Poster'] = lead.dataset.storyDetailPoster;
        }
        const picture = document.createElement('picture');
        const poster = document.createElement('img');
        poster.src = lead.dataset.storyDetailPoster;
        poster.width = 720; poster.height = 1080;
        poster.alt = ''; poster.loading = 'lazy'; poster.decoding = 'async';
        picture.append(poster);
        const video = document.createElement('video');
        video.width = 1080; video.height = 1620;
        video.muted = true; video.loop = true; video.playsInline = true;
        video.preload = 'none'; video.setAttribute('aria-hidden', 'true');
        video.tabIndex = -1;
        player.append(picture, video);
        hero.append(player);
      }
      // Products without uploaded photography retain their existing placeholder.
      if (!hero.children.length) {
        const placeholder = lead.querySelector('.gac-card__placeholder');
        if (placeholder) hero.append(placeholder.cloneNode(true));
      }
      hero.classList.toggle('gac-story__hero--single', hero.querySelectorAll(':scope > img, :scope > video, :scope > svg, :scope > giesto-campaign-video').length < 2);
      const single = hero.classList.contains('gac-story__hero--single');
      story.classList.toggle('gac-story--single-lead', single);
      story.classList.toggle('gac-story--single-framed', single && cards.length === 1);
      if (index === 0) this.#deferBehindBanner(hero);
      if (cards.length === 1) {
        const frame = document.createElement('div');
        frame.className = 'gac-story__hero-frame';
        frame.classList.toggle('gac-story__hero-frame--single', single);
        const card = this.#prepareCard(lead, true);
        card.classList.add('gac-story__single-card');
        const favorite = card.querySelector('giesto-wishlist-toggle');
        if (favorite) card.append(favorite);
        const badges = card.querySelector('.gac-card__badges');
        if (badges) card.append(badges);
        card.querySelector('.gac-card__media')?.remove();
        card.querySelector('.gac-card__meta')?.remove();
        leadMeta.querySelector('.gac-story__lead-swatches')?.remove();
        frame.append(hero, card);
        story.append(leadMeta, frame);
      } else {
        const colors = this.#makeRail(cards.map(card => this.#prepareCard(card, true, true)), lead.dataset.storyProduct, true);
        if (cards.length <= 2) colors.classList.add('gac-story__colors--few');
        story.append(leadMeta, hero, colors);
      }
      return story;
    }

    #makeProductRow(families, layout) {
      const story = this.#section(layout, this.#text('Koleksiyon ürünleri', 'Collection products'));
      const items = families.map(cards => this.#makeFamily(cards, layout === 'rail'));
      if (layout === 'rail') {
        story.append(this.#makeRail(items, families[0][0].dataset.storyProduct, false));
      } else {
        const grid = document.createElement('ul');
        grid.className = 'gac-story__grid';
        grid.setAttribute('role', 'list');
        const template = document.createElement('template');
        template.dataset.storyMount = '';
        template.content.append(...items);
        story.append(grid, template);
      }
      return story;
    }

    #makeFamily(cards, packshot) {
      const item = document.createElement('li');
      item.className = 'gac-story__family';
      item.dataset.family = cards[0].dataset.storyFamily;
      const active = document.createElement('div');
      active.dataset.storyActive = '';
      const variants = document.createElement('template');
      variants.dataset.storyVariants = '';
      for (const source of cards) {
        // A family is the list item; its colour cards are ordinary containers.
        const card = document.createElement('div');
        for (const attr of source.attributes) card.setAttribute(attr.name, attr.value);
        card.append(...source.childNodes);
        this.#prepareCard(card, packshot, cards.length > 1);
        if (!active.firstChild) active.append(card);
        else variants.content.append(card);
      }
      item.append(active, variants);
      if (cards.length > 1) {
        const swatches = document.createElement('div');
        swatches.className = 'gac-story__swatches';
        swatches.setAttribute('role', 'group');
        swatches.setAttribute('aria-label', this.#text('Renk seçenekleri', 'Color options'));
        for (const [index, card] of cards.entries()) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.storyColorId = card.dataset.storyProduct;
          button.setAttribute('aria-label', card.dataset.storyTitle);
          button.setAttribute('aria-pressed', String(index === 0));
          button.title = card.dataset.storyColor || card.dataset.storyTitle;
          const chip = document.createElement('span');
          chip.style.background = card.dataset.storyChip || '#d8d8d8';
          button.append(chip);
          swatches.append(button);
        }
        item.append(swatches);
      }
      return item;
    }

    #makeRail(items, id, colors) {
      const rail = document.createElement('div');
      rail.className = 'gac-story__colors';
      const controls = document.createElement('div');
      controls.className = 'gac-story__controls';
      const counter = document.createElement('span');
      counter.className = 'gac-story__counter';
      const prev = this.#arrow('previous', colors);
      const next = this.#arrow('next', colors);
      controls.append(counter, prev, next);
      const track = document.createElement('ul');
      track.className = 'gac-story__track';
      track.setAttribute('role', 'list');
      track.setAttribute('aria-label', colors ? this.#text('Renk seçenekleri', 'Color options') : this.#text('Koleksiyon ürünleri', 'Collection products'));
      track.id = `gac-story-colors-${id}`;
      prev.setAttribute('aria-controls', track.id);
      next.setAttribute('aria-controls', track.id);
      const template = document.createElement('template');
      template.dataset.storyMount = '';
      template.content.append(...items);
      rail.append(controls, track, template);
      return rail;
    }

    #arrow(direction, colors) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gac-story__arrow';
      button.dataset.storyArrow = direction;
      button.setAttribute('aria-label', direction === 'next'
        ? (colors ? this.#text('Sonraki renkler', 'Next colors') : this.#text('Sonraki ürünler', 'Next products'))
        : (colors ? this.#text('Önceki renkler', 'Previous colors') : this.#text('Önceki ürünler', 'Previous products')));
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '24');
      svg.setAttribute('height', '24');
      svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS(svg.namespaceURI, 'path');
      path.setAttribute('d', direction === 'next' ? 'm9 4 8 8-8 8' : 'm15 4-8 8 8 8');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1');
      svg.append(path);
      button.append(svg);
      return button;
    }

    #mountColors(story, force = false) {
      const template = story?.querySelector('template[data-story-mount]');
      if (!template) return;
      const track = story.querySelector('.gac-story__track, .gac-story__grid');
      if (!force && story.querySelector('.gac-story__colors')?.getBoundingClientRect().top > innerHeight + 80) {
        if (!this.#colorObserver) this.#colorObserver = new IntersectionObserver(entries => {
          for (const entry of entries) if (entry.isIntersecting) { this.#colorObserver.unobserve(entry.target); this.#mountColors(entry.target.closest('.gac-story'), true); }
        }, { rootMargin: '80px' });
        const colors = story.querySelector('.gac-story__colors');
        const mount = () => { this.#colorObserver.unobserve(colors); this.#mountColors(story, true); };
        colors.addEventListener('focusin', mount, { once: true, signal: this.#carouselEvents.signal });
        colors.addEventListener('pointerdown', mount, { once: true, signal: this.#carouselEvents.signal });
        this.#colorObserver.observe(colors);
        const counter = story.querySelector('.gac-story__counter');
        if (counter) counter.textContent = `1 / ${template.content.children.length}`;
        return;
      }
      track.append(template.content);
      template.remove();
      this.#fadeIn(track);
      if (track.classList.contains('gac-story__grid')) return;
      const update = () => {
        const rect = track.getBoundingClientRect();
        let lastVisible = 0;
        for (const [index, card] of [...track.children].entries()) {
          if (card.getBoundingClientRect().right <= rect.right + 2) lastVisible = index + 1;
        }
        const counter = story.querySelector('.gac-story__counter');
        const value = `${lastVisible || 1} / ${track.children.length}`;
        if (counter.textContent !== value) counter.textContent = value;
        story.querySelector('[data-story-arrow="previous"]').disabled = track.scrollLeft <= 2;
        story.querySelector('[data-story-arrow="next"]').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        const overflow = track.scrollWidth > track.clientWidth + 2;
        for (const arrow of story.querySelectorAll('[data-story-arrow]')) arrow.hidden = !overflow;
      };
      let pending = false;
      const schedule = () => { if (pending) return; pending = true; requestAnimationFrame(() => { pending = false; if (track.isConnected) update(); }); };
      track.addEventListener('scroll', schedule, { passive: true, signal: this.#carouselEvents.signal });
      window.addEventListener('resize', schedule, { signal: this.#carouselEvents.signal });
      schedule();
    }

    #onClick = event => {
      if (event.target.closest('[data-story-retry]')) this.activate();
      if (event.target.closest('[data-story-back]')) {
        if (this.closest('giesto-collection-hub')) document.querySelector('.gnr [data-gnr-layout="uniform"]')?.click();
        const collection = this.closest('[data-gac-story-enabled]');
        collection?.querySelector('[data-gac-view-option][value="4"]')?.click();
        collection?.querySelector('[data-gac-view-trigger]')?.focus({ preventScroll: true });
      }
      const swatch = event.target.closest('[data-story-color-id]');
      if (swatch) {
        const family = swatch.closest('.gac-story__family');
        const variants = family.querySelector('[data-story-variants]').content;
        const selected = [...variants.children].find(card => card.dataset.storyProduct === swatch.dataset.storyColorId);
        if (selected) {
          const active = family.querySelector('[data-story-active]');
          variants.append(active.firstElementChild);
          active.append(selected);
          for (const button of family.querySelectorAll('[data-story-color-id]')) button.setAttribute('aria-pressed', String(button === swatch));
        }
      }
      const arrow = event.target.closest('[data-story-arrow]');
      if (!arrow) return;
      const track = arrow.closest('.gac-story__colors').querySelector('.gac-story__track');
      const card = track.firstElementChild;
      if (!card) return;
      const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap);
      track.scrollBy({ left: step * (arrow.dataset.storyArrow === 'next' ? 1 : -1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
  }
  customElements.define('giesto-collection-stories', GiestoCollectionStories);
})();
